import React from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { CardTypes } from 'features/payment/types';
import { mapPaymentMethodCardTypes } from 'features/payment/utils';

import { primitive } from 'styles/constants';

const BlankCard = Box.withConfig({
  backgroundColor: primitive.$greyFour,
  height: '$5',
  width: '$6',
});

interface ICardTypeIcon {
  methodType: string;
  isMediumSize?: boolean;
  isLargeSize?: boolean;
  isNoBorder?: boolean;
  showGenericIcon?: boolean;
}

export const cardIconRenderer = ({
  methodType,
  isMediumSize,
  isLargeSize,
  isNoBorder = false,
  showGenericIcon = false,
}: ICardTypeIcon): JSX.Element => {
  const cardType = mapPaymentMethodCardTypes(methodType);

  if (!cardType) {
    return <BlankCard />;
  }
  // Default icon size to null (small)
  let iconSize: { size: string } | undefined;
  if (isMediumSize) {
    iconSize = { size: 'md' };
  } else if (isLargeSize) {
    iconSize = { size: 'xl' };
  }

  if (showGenericIcon) {
    return <Icon variant="smallCreditCardWhite" {...iconSize} />;
  }

  const methodTypeUpper = methodType.toLocaleUpperCase();
  const cardTypeMethod = methodTypeUpper === 'MC' ? CardTypes.MASTERCARD : methodTypeUpper;

  switch (cardTypeMethod) {
    case CardTypes.VISA:
      return (
        <Icon
          accessibilityHidden
          title="Visa"
          variant={isNoBorder ? 'visaNoBorder' : 'visa'}
          {...iconSize}
        />
      );
    case CardTypes.MASTERCARD:
      return (
        <Icon
          accessibilityHidden
          title="Mastercard"
          variant={isNoBorder ? 'mastercardNoBorder' : 'mastercard'}
          {...iconSize}
        />
      );
    case CardTypes.AMEX:
      return <Icon accessibilityHidden title="Amex" variant="amex" {...iconSize} />;
    case CardTypes.DISCOVER:
      return (
        <Icon
          accessibilityHidden
          title="Discover"
          variant={isNoBorder ? 'discoverNoBorder' : 'discover'}
          {...iconSize}
        />
      );
    case CardTypes.DINERS:
      return (
        <Icon
          accessibilityHidden
          title="Diners"
          variant={isNoBorder ? 'dinersNoBorder' : 'diners'}
          {...iconSize}
        />
      );
    case CardTypes.JCB:
      return <Icon title="JCB" variant={isNoBorder ? 'jcbNoBorder' : 'jcb'} {...iconSize} />;
    case CardTypes.APPLE_PAY:
      return (
        <Icon
          accessibilityHidden
          title="Apple Pay"
          variant={isNoBorder ? 'applepayNoBorder' : 'applepay'}
          {...iconSize}
        />
      );
    case CardTypes.GOOGLE_PAY:
      return <Icon accessibilityHidden title="Google Pay" variant="googlePayBrand" {...iconSize} />;
    case CardTypes.PAYPAL:
      return (
        <Icon
          accessibilityHidden
          title="PayPal"
          variant={isNoBorder ? 'paypalNoBorder' : 'paypal'}
          {...iconSize}
        />
      );
    case CardTypes.VENMO:
      return (
        <Icon
          accessibilityHidden
          title="Venmo"
          variant={isNoBorder ? 'venmoNoBorder' : 'venmo'}
          {...iconSize}
        />
      );
    case CardTypes.CASH:
      return (
        <Icon
          accessibilityHidden
          title="Cash"
          variant={isNoBorder ? 'cashNoBorder' : 'cash'}
          {...iconSize}
        />
      );

    case CardTypes.CASHAPP:
      return (
        <Icon
          accessibilityHidden
          title="Cash App"
          variant={isNoBorder ? 'cashAppWhiteBorder' : 'cashApp'}
          {...iconSize}
        />
      );
    default:
      return <BlankCard />;
  }
};
